import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Trophy, Medal, Target, CreditCard, Calendar, Gavel, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TorneoPublicoPage() {
  const { torneoId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('posiciones');

  useEffect(() => {
    loadTorneo();
  }, [torneoId]);

  const loadTorneo = async () => {
    try {
      const res = await axios.get(`${API}/torneos/publico/${torneoId}`);
      setData(res.data);
    } catch (err) {
      setError('Torneo no encontrado');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Card className="p-8 text-center bg-slate-800 border-slate-700">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <p className="text-white font-medium">{error || 'Error al cargar'}</p>
        </Card>
      </div>
    );
  }

  const { torneo, fechas, posiciones, goleadores, sanciones_activas } = data;

  const TABS = [
    { id: 'posiciones', label: 'Posiciones', icon: Medal },
    { id: 'fixture', label: 'Fixture', icon: Calendar },
    { id: 'goleadores', label: 'Goleadores', icon: Target },
    { id: 'sanciones', label: 'Sanciones', icon: Gavel },
  ];

  return (
    <div className="min-h-screen bg-slate-900" data-testid="torneo-publico">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 py-6 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
            <Trophy className="w-7 h-7 text-slate-900" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1" data-testid="torneo-publico-title">{torneo.nombre}</h1>
          <p className="text-slate-400">
            {torneo.categoria_nombre}
            {torneo.estado === 'finalizado' && torneo.campeon && (
              <span className="ml-3 text-amber-400 font-semibold">Campeon: {torneo.campeon}</span>
            )}
          </p>
          {torneo.estado === 'finalizado' && (
            <span className="inline-block mt-2 px-3 py-1 bg-slate-700 text-slate-400 rounded text-sm">Torneo Finalizado</span>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 mt-4">
        <div className="flex gap-1 bg-slate-800 p-1 rounded-lg overflow-x-auto">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex-1 justify-center ${
                  activeTab === tab.id ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'
                }`}
                data-testid={`publico-tab-${tab.id}`}>
                <Icon className="w-4 h-4" /> <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Posiciones */}
        {activeTab === 'posiciones' && (
          posiciones.length === 0 ? (
            <Card className="p-6 text-center bg-slate-800 border-slate-700"><p className="text-slate-400">Sin datos de posiciones aun.</p></Card>
          ) : (
            <Card className="overflow-hidden border-0 shadow-lg bg-slate-800">
              <table className="w-full text-sm" data-testid="publico-posiciones">
                <thead>
                  <tr className="bg-slate-700 text-white text-left">
                    <th className="px-2 sm:px-3 py-3 w-10 text-center">#</th>
                    <th className="px-2 sm:px-3 py-3">Equipo</th>
                    <th className="px-2 py-3 text-center">PJ</th>
                    <th className="px-2 py-3 text-center">PG</th>
                    <th className="px-2 py-3 text-center">PE</th>
                    <th className="px-2 py-3 text-center">PP</th>
                    <th className="px-2 py-3 text-center hidden sm:table-cell">GF</th>
                    <th className="px-2 py-3 text-center hidden sm:table-cell">GC</th>
                    <th className="px-2 py-3 text-center">DIF</th>
                    <th className="px-2 sm:px-3 py-3 text-center font-bold">PTS</th>
                  </tr>
                </thead>
                <tbody>
                  {posiciones.map(eq => (
                    <tr key={eq.equipo} className={`border-b border-slate-700 ${eq.pos <= 3 ? 'bg-slate-750' : ''}`}>
                      <td className="px-2 sm:px-3 py-2.5 text-center font-bold text-slate-400">
                        {eq.pos === 1 ? <Medal className="w-4 h-4 text-amber-400 inline" /> : eq.pos}
                      </td>
                      <td className="px-2 sm:px-3 py-2.5 font-semibold text-white text-sm">{eq.equipo}</td>
                      <td className="px-2 py-2.5 text-center text-slate-400">{eq.pj}</td>
                      <td className="px-2 py-2.5 text-center text-emerald-400">{eq.pg}</td>
                      <td className="px-2 py-2.5 text-center text-amber-400">{eq.pe}</td>
                      <td className="px-2 py-2.5 text-center text-red-400">{eq.pp}</td>
                      <td className="px-2 py-2.5 text-center text-slate-400 hidden sm:table-cell">{eq.gf}</td>
                      <td className="px-2 py-2.5 text-center text-slate-400 hidden sm:table-cell">{eq.gc}</td>
                      <td className="px-2 py-2.5 text-center font-medium">
                        <span className={eq.dif > 0 ? 'text-emerald-400' : eq.dif < 0 ? 'text-red-400' : 'text-slate-500'}>
                          {eq.dif > 0 ? `+${eq.dif}` : eq.dif}
                        </span>
                      </td>
                      <td className="px-2 sm:px-3 py-2.5 text-center font-bold text-lg text-white">{eq.pts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )
        )}

        {/* Fixture */}
        {activeTab === 'fixture' && (
          fechas.length === 0 ? (
            <Card className="p-6 text-center bg-slate-800 border-slate-700"><p className="text-slate-400">Sin fechas programadas.</p></Card>
          ) : (
            <div className="space-y-4">
              {fechas.map(fecha => (
                <Card key={fecha.id} className="p-4 bg-slate-800 border-slate-700">
                  <div className="mb-3">
                    <h3 className="font-semibold text-white">{fecha.nombre}</h3>
                    <div className="flex gap-2 text-xs text-slate-400 mt-1">
                      <span className={`px-1.5 py-0.5 rounded ${fecha.tipo === 'eliminacion' ? 'bg-purple-900/50 text-purple-400' : 'bg-blue-900/50 text-blue-400'}`}>
                        {fecha.tipo === 'eliminacion' ? 'Eliminacion' : 'Grupo'}
                      </span>
                      {fecha.fecha_programada && <span>{fecha.fecha_programada}</span>}
                      {fecha.cancha && <span>| {fecha.cancha}</span>}
                      {fecha.estado === 'suspendida' && (
                        <span className="bg-red-900/50 text-red-400 px-1.5 py-0.5 rounded">Suspendida</span>
                      )}
                    </div>
                  </div>
                  {fecha.partidos?.length === 0 ? (
                    <p className="text-xs text-slate-500">Sin partidos</p>
                  ) : (
                    <div className="space-y-2">
                      {fecha.partidos.map(p => (
                        <div key={p.id} className="flex items-center justify-between p-2.5 bg-slate-700 rounded">
                          <span className="text-sm font-medium text-white flex-1 text-right">{p.equipo_local}</span>
                          {p.estado === 'finalizado' ? (
                            <span className="mx-3 text-emerald-400 font-bold text-lg min-w-[60px] text-center">{p.goles_local} - {p.goles_visitante}</span>
                          ) : (
                            <span className="mx-3 text-slate-500 text-sm min-w-[60px] text-center">vs</span>
                          )}
                          <span className="text-sm font-medium text-white flex-1">{p.equipo_visitante}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )
        )}

        {/* Goleadores */}
        {activeTab === 'goleadores' && (
          goleadores.length === 0 ? (
            <Card className="p-6 text-center bg-slate-800 border-slate-700"><p className="text-slate-400">Sin goles registrados.</p></Card>
          ) : (
            <Card className="overflow-hidden border-0 shadow-lg bg-slate-800">
              <table className="w-full text-sm" data-testid="publico-goleadores">
                <thead><tr className="bg-slate-700 text-white"><th className="px-3 py-3 w-12 text-center">#</th><th className="px-3 py-3">Jugador</th><th className="px-3 py-3">Equipo</th><th className="px-3 py-3 text-center">Goles</th></tr></thead>
                <tbody>
                  {goleadores.map((g, i) => (
                    <tr key={i} className="border-b border-slate-700">
                      <td className="px-3 py-2.5 text-center font-bold text-slate-400">{i + 1}</td>
                      <td className="px-3 py-2.5 font-semibold text-white">{g.jugador}</td>
                      <td className="px-3 py-2.5 text-slate-400">{g.equipo}</td>
                      <td className="px-3 py-2.5 text-center font-bold text-lg text-white">{g.goles}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )
        )}

        {/* Sanciones */}
        {activeTab === 'sanciones' && (
          sanciones_activas.length === 0 ? (
            <Card className="p-6 text-center bg-slate-800 border-slate-700"><p className="text-slate-400">Sin sanciones activas.</p></Card>
          ) : (
            <div className="space-y-3">
              {sanciones_activas.map(s => (
                <Card key={s.id} className="p-4 bg-slate-800 border-slate-700 border-l-4 border-l-red-500">
                  <p className="font-semibold text-white">{s.jugador_nombre} <span className="text-slate-400 font-normal">({s.equipo})</span></p>
                  <p className="text-sm text-slate-400 mt-1">{s.causa}</p>
                  <div className="flex gap-3 mt-2 text-xs">
                    <span className={`px-2 py-0.5 rounded ${s.tipo === 'tribunal' ? 'bg-purple-900/50 text-purple-400' : s.tipo === 'roja' ? 'bg-red-900/50 text-red-400' : 'bg-amber-900/50 text-amber-400'}`}>
                      {s.tipo === 'tribunal' ? 'Tribunal' : s.tipo === 'roja' ? 'Roja directa' : 'Amarillas'}
                    </span>
                    <span className="text-slate-400">{s.fechas_cumplidas}/{s.fechas_castigo} fechas cumplidas</span>
                  </div>
                </Card>
              ))}
            </div>
          )
        )}
      </div>

      {/* Footer */}
      <footer className="bg-slate-800 border-t border-slate-700 py-4 mt-8">
        <p className="text-center text-xs text-slate-500">Powered by Kinosis</p>
      </footer>
    </div>
  );
}
